import {
  Component,
  Input,
  ViewEncapsulation,
  ElementRef,
  ViewChild,
  EventEmitter,
  Output,
  AfterViewChecked,
  forwardRef,
  AfterViewInit,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import { UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validator, ControlValueAccessor } from '@angular/forms';
import { IMaskDirective  } from 'angular-imask';
import { WindowService } from 'src/shared/providers/window.service';
import {
  checkFocused,
  checkFocusedout,
  buildIntlTelInput,
  configureIntlTelInputListener,
  toggleFlagContainerVisibility,
  shouldValidate,
  updateMask,
  setInputValue,
  getCountryCodeValue
} from './international-number.utils';
@Component({
    selector: 'tidy-input-international-phone',
    templateUrl: 'input-international-phone.component.html',
    styleUrls: ['./input-international-phone.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputInternationalPhoneComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InputInternationalPhoneComponent),
            multi: true
        }
    ],
    standalone: false
})
export class InputInternationalPhoneComponent implements ControlValueAccessor, AfterViewChecked, AfterViewInit, OnChanges, OnInit, Validator, OnDestroy {
  @ViewChild('phoneInput', {static: false, read: IMaskDirective}) maskDirective: IMaskDirective<any>;
  @ViewChild('phoneInput', {static: false}) phoneInputRef: ElementRef;

  @Input() public label: string;
  @Input() public helperText: string;
  @Input() public icon: string;
  @Input() public activeIcon: string;
  @Input() public form: UntypedFormControl;
  @Input() public submitted: boolean;
  @Input() public errorMessage: string;
  @Input() public type: string;
  @Input() public noBorder: boolean;
  @Input() public inputMode: string;
  @Input() autoComplete = false;
  @Input() public initialMask: any;

  focused = false;
  touchedState = false;
  phoneValue: string;
  onChange: (value: string) => void;
  disableElement = false;

  shouldntUpdateCountryInput = false;
  flagsContainer: HTMLElement;
  validateInternationFn = () => true;
  @Output() countryCodeChange: EventEmitter<string> = new EventEmitter<string>();
  intlTel: Readonly<any>;
  @Input() defaultCountryCode = 'US';
  setOnCompleteEvent = false;
  shouldUpdateMask = false;
  writeValueChanged = false;
  maskInitialized = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private windowService: WindowService
  ) {}

  ngOnInit() {
    const isSecondaryLayer = localStorage.getItem('isPrimaryLayer') === 'false' && this.windowService.isDesktopRes;
    if (isSecondaryLayer) {
      this.renderer.addClass(this.el.nativeElement, 'secondary-layer');
    }
  }

  ngAfterViewInit() {
    if (this.initialMask && !this.maskInitialized && this.maskDirective && this.phoneInputRef) {
      this.initIntLTel();
      this.maskInitialized = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.initialMask && changes.initialMask.currentValue && !this.maskInitialized && this.maskDirective && this.phoneInputRef) {
      this.initIntLTel();
      this.maskInitialized = true;
    }
  }

  initIntLTel() {
    // Check if both maskDirective and phoneInputRef are available
    if (!this.maskDirective || !this.phoneInputRef) {
      return;
    }

    const maskRef = this.maskDirective.maskRef;
    const input = this.phoneInputRef.nativeElement;

    if (!input) {
      return;
    }

    this.intlTel = buildIntlTelInput(input, this.defaultCountryCode);
    configureIntlTelInputListener(input, this.intlTel, this.countryCodeChange, maskRef);
    this.flagsContainer = this.intlTel.countryContainer;
    this.validateInternationFn = shouldValidate.bind(null, this.intlTel);
    /* toggleFlagContainerVisibility('none', this.flagsContainer); */
    this.countryCodeChange.emit(getCountryCodeValue(this.intlTel));
    this.validateInternationFn = shouldValidate.bind(null, this.intlTel);
  }

  ngAfterViewChecked() {
    // Try to initialize if not already done and dependencies are available
    if (!this.maskInitialized && this.initialMask && this.maskDirective && this.phoneInputRef) {
      this.initIntLTel();
      this.maskInitialized = true;
    }

    if (this.maskInitialized) {
      this.configureCountrySelection();
    }
  }

  configureCountrySelection() {
    if (!this.maskDirective || !this.maskDirective.maskRef) {
      return;
    }

    const maskRef = this.maskDirective.maskRef;

    if (!this.shouldntUpdateCountryInput && maskRef.unmaskedValue && this.writeValueChanged) {
      toggleFlagContainerVisibility('block', this.flagsContainer);
      setInputValue(this.intlTel, `+${maskRef.unmaskedValue}`);

      this.shouldntUpdateCountryInput = true;
    }

    if (!this.setOnCompleteEvent && maskRef.unmaskedValue) {
      maskRef.on('complete', this.handleMaskCompleteEvent.bind(this));

      this.setOnCompleteEvent = true;
    }

    if (!this.shouldUpdateMask && getCountryCodeValue(this.intlTel)) {
      updateMask(this.intlTel, maskRef);

      this.shouldUpdateMask = true;
    }
  }

  validate(_: UntypedFormControl) {
    // Get the unmasked value (digits only) from the mask
    const unmaskedValue = this.maskDirective?.maskRef?.unmaskedValue || '';

    // If no input, don't show error (let required validator handle it)
    if (!unmaskedValue) {
      return null;
    }

    // First check if intl-tel-input utils are loaded and can validate
    const validationError = this.intlTel?.getValidationError();
    const utilsLoaded = validationError !== -99;

    if (utilsLoaded && this.intlTel) {
      // Utils loaded - use intl-tel-input's validation
      const isInValid = this.intlTel.getNumber() && shouldValidate(this.intlTel);
      return isInValid ? { phone: { valid: false } } : null;
    }

    // Fallback validation when utils not loaded
    const countryData = this.intlTel?.getSelectedCountryData();
    const dialCode = countryData?.dialCode || '1';
    const isUS = dialCode === '1';

    // Basic length validation
    const requiredDigits = isUS ? 10 : 7;
    const maxDigits = isUS ? 10 : 15;

    if (unmaskedValue.length < requiredDigits || unmaskedValue.length > maxDigits) {
      return { phone: { valid: false } };
    }

    // For US numbers, validate area code (first 3 digits can't be 0XX or 1XX)
    if (isUS && unmaskedValue.length >= 3) {
      const areaCode = unmaskedValue.substring(0, 3);
      if (areaCode.startsWith('0') || areaCode.startsWith('1')) {
        return { phone: { valid: false } };
      }
      // Also check exchange code (digits 4-6 can't be 0XX or 1XX)
      if (unmaskedValue.length >= 6) {
        const exchangeCode = unmaskedValue.substring(3, 6);
        if (exchangeCode.startsWith('0') || exchangeCode.startsWith('1')) {
          return { phone: { valid: false } };
        }
      }
    }

    return null;
  }

  checkFocused() {
    checkFocused(this.flagsContainer);
  }

  checkFocusedout(e) {
    const isEmpty = e.target.value === '';

    checkFocusedout(e, this.flagsContainer, isEmpty);
  }

  changeTouch = () => {
    this.touchedState = true;
  }

  setFocused(status) {
    this.focused = status;
  }

  ngOnDestroy() {
    this.intlTel?.destroy();
  }

  writeValue(value: string) {
    if (this.intlTel) {
      setInputValue(this.intlTel, value);
    }

    this.writeValueChanged = !!value?.includes('+');

    this.phoneValue = value;
  }

  registerOnChange(fn: (value: string) => void) {
    this.onChange = fn;
  }

  handleMaskCompleteEvent() {
    this.updateFormValue();
  }

  onInputChange() {
    // Update form value on every input change (not just mask complete)
    // This ensures validation runs when user deletes characters
    this.updateFormValue();
  }

  private updateFormValue() {
    if (!this.onChange) {
      return;
    }

    // Get the full phone number with country code
    // Try intlTel.getNumber() first, fall back to constructing it manually
    let phoneNumber = this.intlTel?.getNumber();

    // If getNumber() returns empty (utils not loaded), construct the number manually
    if (!phoneNumber) {
      const countryData = this.intlTel?.getSelectedCountryData();
      const dialCode = countryData?.dialCode || '1';
      const unmaskedValue = this.maskDirective?.maskRef?.unmaskedValue || '';
      phoneNumber = unmaskedValue ? `+${dialCode}${unmaskedValue}` : '';
    }

    this.onChange(phoneNumber);
  }

  registerOnTouched(_fn: any) {
    // Required by ControlValueAccessor interface
  }

  setDisabledState(isDisabled: boolean) {
    this.disableElement = isDisabled;
  }
}
